// 用户相关类型
export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: UserRole;
  status: UserStatus;
  avatar?: string;
  createTime: string;
  updateTime?: string;
}

export type UserRole = 'admin' | 'moderator' | 'user';
export type UserStatus = 'active' | 'inactive' | 'pending';

// 旧订单相关类型（已废弃）
export interface OldOrder {
  id: string;
  orderNo: string;
  customer: string;
  amount: number;
  status: OldOrderStatus;
  createTime: string;
  updateTime?: string;
}

export type OldOrderStatus = 'pending' | 'processing' | 'shipped' | 'completed' | 'cancelled';

// 统计数据类型
export interface Statistics {
  totalUsers: number;
  totalOrders: number;
  totalRevenue: number;
  conversionRate: number;
}

// 图表数据类型
export interface ChartData {
  name: string;
  value: number;
  [key: string]: any;
}

export interface ChartOption {
  title?: {
    text: string;
    left?: string | number;
    top?: string | number;
  };
  tooltip?: {
    trigger?: string;
    formatter?: string | Function;
  };
  legend?: {
    data?: string[];
    orient?: string;
    left?: string | number;
    top?: string | number;
  };
  xAxis?: {
    type: string;
    data?: any[];
    name?: string;
    position?: string;
  };
  yAxis?: {
    type: string;
    name?: string;
    position?: string;
  } | Array<{
    type: string;
    name?: string;
    position?: string;
  }>;
  series: Array<{
    name: string;
    type: string;
    data: any[];
    [key: string]: any;
  }>;
  [key: string]: any;
}

// 系统设置类型
export interface SystemSettings {
  siteName: string;
  siteDescription?: string;
  siteKeywords?: string;
  defaultLanguage: string;
  timezone: string;
  pageSize: number;
  enableRegistration: boolean;
  enableEmailVerification: boolean;
  enableNotifications: boolean;
  enableAuditLog?: boolean;
  emailSettings: EmailSettings;
  securitySettings?: SecuritySettings;
}

export interface EmailSettings {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  fromEmail: string;
  fromName: string;
}

export interface SecuritySettings {
  enableTwoFactor?: boolean;
  enableLoginLog?: boolean;
  sessionTimeout?: number;
  maxLoginAttempts?: number;
}

// 通用API响应类型
export interface ApiResponse<T = any> {
  status: number; // 1-成功，0-失败
  msg: string | null; // 消息提示
  data: T; // 返回数据
}

export interface PaginationParams {
  page: number;
  pageSize: number;
  total?: number;
}

export interface Page<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  empty: boolean;
  first: boolean;
  last?: boolean;
  numberOfElements?: number;
}

export interface PaginatedResponse<T> {
  status: number;
  msg: string;
  data: Page<T>;
}

// 路由类型
export interface RouteConfig {
  path: string;
  component: React.ComponentType;
  exact?: boolean;
  children?: RouteConfig[];
}

// 菜单项类型
export interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  path?: string;
}

// API菜单项类型（用于后端API返回的菜单数据）
export interface ApiMenuItem {
  menuId: number;
  menuCode: string;
  menuName: string;
  parentMenuId: number;
  menuLevel: number;
  menuType: number;
  menuUrl: string;
  menuIcon: string;
  sortOrder: number;
  children?: ApiMenuItem[];
  permissions: string[];
}

// 表单类型
export interface FormField {
  name: string;
  label: string;
  type: 'input' | 'select' | 'textarea' | 'switch' | 'date' | 'number';
  required?: boolean;
  rules?: any[];
  options?: Array<{ label: string; value: any }>;
  placeholder?: string;
  [key: string]: any;
}

// 文件上传类型
export interface UploadFile {
  uid: string;
  name: string;
  status: 'uploading' | 'done' | 'error' | 'removed';
  url?: string;
  size?: number;
  type?: string;
}

// 通知类型
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: string;
  read: boolean;
}

// 主题类型
export interface Theme {
  primaryColor: string;
  borderRadius: number;
  compact: boolean;
  darkMode: boolean;
}

// 权限类型
export interface Permission {
  id: string;
  name: string;
  code: string;
  description?: string;
}

export interface Role {
  id: string;
  name: string;
  code: string;
  description?: string;
  permissions: Permission[];
}

// 经销商相关类型
export interface Dealer {
  id: number;
  dealerCode: string;
  dealerName: string;
  dealerLevel: number;
  adminId: number;
  parentDealerId?: number | null;
  dealerType: number; // 1-超级经销商，0-普通经销商
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: string;
  status: number; // 1-启用，0-禁用
  remark?: string;
  createBy?: string;
  updateBy?: string;
  createTime: string;
  updateTime: string;
}

// 经销商VO（包含文本字段）
export interface DealerVO extends Dealer {
  dealerTypeText?: string; // 经销商类型文本
  statusText?: string; // 状态文本
}

// 创建/更新经销商请求参数（DTO）
export interface DealerRequest {
  dealerCode: string;
  dealerName: string;
  dealerLevel: number;
  adminId: number;
  parentDealerId?: number | null;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: string;
  status: number;
  remark?: string;
}

// 经销商查询参数
export interface DealerQueryRequest {
  id?: number;
  dealerCode?: string;
  dealerName?: string;
  dealerLevel?: number;
  dealerType?: number;
  status?: number;
  adminId?: number;
  parentDealerId?: number;
  contactPerson?: string;
  contactPhone?: string;
  pageNo?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

// 认证相关类型 - 根据API文档定义
export interface LoginRequest {
  username: string;
  password: string;
  captcha?: string;
  captchaKey?: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken?: string;
  tokenType: string;
  expiresIn: number;
  userInfo: UserInfo;
  menus: ApiMenuItem[];
  permissions: string[];
}

export interface UserInfo {
  userId: number;
  username: string;
  realName: string;
  email: string;
  phone: string;
  avatar: string;
  dealerId: number;
  dealerName: string;
  dealerType?: number; // 0-普通经销商, 1-超级经销商
  roles: string[];
  lastLoginTime: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

export interface ValidateTokenResponse {
  valid: boolean;
}

// 状态码枚举
export enum ApiStatus {
  SUCCESS = 1,
  FAILED = 0,
  WARNING = 2,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  LOGIN_EXPIRED = 10,
  INVALID_CREDENTIALS = 2001,
  USER_DISABLED = 2002,
  USER_NOT_FOUND = 2003,
  INSUFFICIENT_PERMISSIONS = 3001,
  SYSTEM_ERROR = 5000,
  API_ERROR = 5001,
}

// 套餐组相关类型
export interface PackageGroup {
  id: number;
  groupName: string;
  groupType: string;
  deviceType: string;
  dealerId?: number;
  status: number;
  description?: string;
  remark?: string;
  createBy: string;
  updateBy?: string;
  createTime: string;
  updateTime?: string;
}

export interface PackageGroupRequest {
  groupName: string;
  groupType: string;
  deviceType: string;
  dealerId?: number;
  status: number;
  description?: string;
  remark?: string;
}

export interface PackageGroupQueryRequest {
  groupName?: string;
  groupType?: string;
  deviceTypeId?: number;
  dealerId?: number;
  status?: number;
  includePublic?: boolean;
  page?: number;
  size?: number;
}

export interface PackageGroupVO {
  id: number;
  groupName: string;
  groupType: string;
  deviceTypeId: number;
  deviceTypeName?: string;
  dealerId?: number;
  dealerName?: string;
  status: number;
  statusText: string;
  description?: string;
  remark?: string;
  createBy: string;
  updateBy?: string;
  createTime: string;
  updateTime?: string;
  packageCount: number;
  activePackageCount: number;
}

export type VipUnit = 'month' | 'day' | 'lifetime';

// 套餐相关类型
export interface Package {
  id: number;
  packageName: string;
  groupId: number;
  originalPrice: number;
  actualPrice: number;
  description?: string;
  status: number;
  sortOrder: number;
  config?: string;
  vipDuration?: number;
  vipUnit?: VipUnit;
  vipMonth?: number;
  remark?: string;
  recommend?: number;        // 是否推荐（0-否，1-是，默认0）
  defaultSelected?: number;  // 是否默认套餐（0-否，1-是，默认0，同一套餐组只允许一个）
  discountType?: number;     // 折扣类型（1-打折，2-一口价，为空表示无折扣）
  discountValue?: number;    // 折扣值（打折时存百分比如80表示8折，一口价时存最终价格）
  continuousPay?: number;    // 连续支付开关（0-关，1-开，默认0，仅测试套餐）
  hideAd?: number;           // 去广告开关（0-关，1-开，默认0）
  createBy: string;
  updateBy?: string;
  createTime: string;
  updateTime?: string;
}

export interface PackageRequest {
  packageName: string;
  groupId: number;
  originalPrice: number;
  actualPrice: number;
  description?: string;
  status: number;
  sortOrder: number;
  config?: string;
  remark?: string;
  vipDuration?: number;
  vipUnit?: VipUnit;
  vipMonth?: number;
  recommend?: number;        // 是否推荐（0-否，1-是，默认0）
  defaultSelected?: number;  // 是否默认套餐（0-否，1-是，默认0，同一套餐组只允许一个）
  discountType?: number;     // 折扣类型（1-打折，2-一口价，为空表示无折扣）
  discountValue?: number;    // 折扣值（打折时存百分比如80表示8折，一口价时存最终价格）
  continuousPay?: number;    // 连续支付开关（0-关，1-开，默认0；更新未传则服务端保持原值）
  hideAd?: number;           // 去广告开关（0-关，1-开，默认0；更新未传则服务端保持原值）
}

export interface PackageQueryRequest {
  packageName?: string;
  groupId?: number;
  groupType?: string;
  deviceType?: string;
  dealerId?: number;
  status?: number;
  minPrice?: number;
  maxPrice?: number;
  includePublic?: boolean;
}

export interface PackageVO {
  id: number;
  packageName: string;
  groupId: number;
  groupName: string;
  deviceType: string;
  originalPrice: number;
  actualPrice: number;
  description?: string;
  status: number;
  statusText: string;
  sortOrder: number;
  config?: string;
  vipDuration?: number;
  vipUnit?: VipUnit;
  vipMonth?: number;
  remark?: string;
  recommend?: number;        // 是否推荐（0-否，1-是）
  defaultSelected?: number;  // 是否默认套餐（0-否，1-是）
  discountType?: number;     // 折扣类型（1-打折，2-一口价，为空表示无折扣）
  discountValue?: number;    // 折扣值（打折时存百分比如80表示8折，一口价时存最终价格）
  continuousPay?: number;    // 连续支付开关（0-关，1-开）
  hideAd?: number;           // 去广告开关（0-关，1-开）
  createBy: string;
  updateBy?: string;
  createTime: string;
  updateTime?: string;
}

// 批量操作类型
export interface BatchStatusRequest {
  ids: number[];
  status: number;
  updateBy: string;
}

export interface SortRequest {
  sortOrder: number;
  updateBy: string;
}

// v2.0 订单相关类型定义 - 包含关联对象信息
export interface Order {
  // ========== 订单基本信息 ==========
  id: number;
  orderNo: string;
  originalAmount: number;
  actualAmount: number;
  payTime?: string;
  paymentTime?: string;
  payMethod?: PayMethod;
  payMethodText?: string; // 🆕 支付方式文本
  paymentChannel?: PaymentChannel;
  paymentChannelText?: string;
  paymentProvider?: PaymentProvider;
  paymentProviderText?: string;
  paymentMethod?: PlatformPaymentMethod;
  paymentMethodText?: string;
  paymentScene?: PaymentScene;
  paymentSceneText?: string;
  orderStatus: OrderStatus;
  orderStatusText?: string; // 🆕 订单状态文本
  deviceBatchId?: number;
  deviceBatchName?: string; // 🆕 设备批次名称
  orderDescription?: string;
  transactionNo?: string;
  thirdTradeNo?: string;
  yeepayUniqueOrderNo?: string;
  yeepayBankOrderId?: string;
  yeepayPrePayTn?: string;
  refundAmount: number;
  refundTime?: string;
  orderSource: OrderSource;
  orderSourceText?: string; // 🆕 订单来源文本
  remark?: string;
  createBy: string;
  updateBy?: string;
  createTime: string; // 注意：API文档中是createTime，不是createdAt
  updateTime?: string;
  
  // ========== 🆕 用户信息（新增） ==========
  userId: number;
  userAccount?: string; // 🆕 用户账号
  userNickname?: string; // 🆕 用户昵称
  userPhone?: string; // 🆕 用户手机号
  
  // ========== 🆕 套餐信息（新增） ==========
  packageId: number;
  packageName?: string; // 🆕 套餐名称
  packageOriginalPrice?: number; // 🆕 套餐原始价格
  packageActualPrice?: number; // 🆕 套餐实际价格
  
  // ========== 🆕 设备信息（新增） ==========
  deviceId: number;
  deviceNum?: string; // 🆕 设备编号
  deviceTypeName?: string; // 🆕 设备类型名称
  deviceFirm?: string; // 🆕 设备厂商
  
  // ========== 🆕 经销商信息（新增） ==========
  dealerId: number;
  dealerName?: string; // 🆕 经销商名称
  dealerCode?: string; // 🆕 经销商编码

  // ========== 多平台支付路径字段 ==========
  clientPlatform?: ClientPlatform;
  merchantConfigId?: number;
  merchantConfigName?: string;
  appCredentialId?: number;
  appCredentialName?: string;
  merchantNo?: string;
  merchantDisplayName?: string;
  merchantNoMasked?: string;
  clientAppId?: string;

  // ========== 充值上下文字段 ==========
  recipientThirdUserId?: string;
  recipientSnapshot?: string;
  rechargeDeviceNum?: string;
  rechargeFirm?: string;
  rechargeSource?: string;
  rechargeSessionId?: string;
  actorPlatformUserId?: string;
  payerPlatformUserId?: string;
}

// 订单查询请求
export interface OrderQueryRequest {
  orderNo?: string;
  dealerId?: number;
  userId?: number;
  packageId?: number;
  deviceId?: number;
  deviceNum?: string;
  deviceBatchId?: number;
  orderStatus?: OrderStatus;
  transactionNo?: string;
  startTime?: number;
  endTime?: number;
  clientPlatform?: ClientPlatform;
  paymentProvider?: PaymentProvider;
  paymentMethod?: PlatformPaymentMethod;
  paymentChannel?: PaymentChannel;
  pageNo: number;
  pageSize: number;
}

// 订单状态枚举
export type OrderStatus = 0 | 1 | 2 | 3 | 4;

// 支付方式枚举
export type PayMethod = 'WECHAT' | 'WECHAT_MINIAPP' | 'ALIPAY' | 'BANK' | 'BALANCE';

// 订单来源枚举
export type OrderSource = 'H5' | 'MINIPROGRAM' | 'APP' | 'ADMIN';

// 订单状态映射
export const ORDER_STATUS_MAP: Record<OrderStatus, { text: string; color: string }> = {
  0: { text: '待支付', color: 'orange' },
  1: { text: '已支付', color: 'green' },
  2: { text: '已取消', color: 'gray' },
  3: { text: '已退款', color: 'red' },
  4: { text: '部分退款', color: 'blue' },
};

// 支付方式映射
export const PAY_METHOD_MAP: Record<PayMethod, string> = {
  'WECHAT': '微信公众号',
  'WECHAT_MINIAPP': '微信小程序',
  'ALIPAY': '支付宝',
  'BANK': '银行卡',
  'BALANCE': '余额',
};

// 订单来源映射
export const ORDER_SOURCE_MAP: Record<OrderSource, string> = {
  'H5': 'H5页面',
  'MINIPROGRAM': '小程序',
  'APP': 'APP',
  'ADMIN': '后台管理',
};

export const CLIENT_PLATFORM_MAP: Record<ClientPlatform, string> = {
  WEAPP: '微信小程序',
  WECHAT_H5: '微信H5',
  H5: '普通H5',
  ALIPAY: '支付宝小程序',
};

export const PAYMENT_PROVIDER_MAP: Record<PaymentProvider, string> = {
  WECHAT_OFFICIAL: '微信官方支付',
  YEEPAY: '易宝聚合支付',
  ALIPAY_OFFICIAL: '支付宝官方支付',
  YUNHUITENG: '云汇腾',
};

export const PLATFORM_PAYMENT_METHOD_MAP: Record<PlatformPaymentMethod, string> = {
  WECHAT_MINIAPP: '微信小程序支付',
  WECHAT_JSAPI: '微信公众号JSAPI',
  ALIPAY_H5: '支付宝H5支付',
  ALIPAY_MINIAPP: '支付宝小程序支付',
};

export const PAYMENT_SCENE_MAP: Record<PaymentScene, string> = {
  WEAPP: '微信小程序',
  WECHAT_H5: '微信H5',
  H5: '普通H5',
  ALIPAY: '支付宝小程序',
};

// ==================== 订单统计相关类型 ====================

// 订单统计数据
export interface OrderStatisticsData {
  statDate: string;              // 统计日期，格式：YYYY-MM-DD
  dealerId: number | null;       // 经销商ID，null表示全平台汇总
  dealerName: string;            // 经销商名称
  orderCount: number;            // 订单总数
  paidOrderCount: number;        // 已支付订单数
  totalAmount: number;           // 总金额（已支付订单的实付金额之和）
  paymentRate?: string;          // 支付率（前端计算或后端返回）
}

// 查询订单统计请求参数
export interface OrderStatisticsQueryRequest {
  startDate: string;             // 开始日期，格式：YYYY-MM-DD
  endDate: string;               // 结束日期，格式：YYYY-MM-DD
  dealerId?: number;             // 经销商ID（可选，管理员可查所有）
  globalOnly?: boolean;          // 是否只查全平台汇总（可选，默认false）
  pageNo?: number;               // 页码（可选，默认1）
  pageSize?: number;             // 每页大小（可选，默认10）
}

// 查询实时统计请求参数
export interface OrderStatisticsRealtimeRequest {
  dealerId?: number;             // 经销商ID（可选）
  globalOnly?: boolean;          // 是否只查全平台汇总（可选）
  allDealers?: boolean;          // 是否查询所有经销商（可选，仅管理员）
}

// 手动重算统计请求参数
export interface OrderStatisticsRecalculateRequest {
  startDate: string;             // 开始日期，格式：YYYY-MM-DD
  endDate: string;               // 结束日期，格式：YYYY-MM-DD
  dealerId?: number;             // 经销商ID（可选，不传则重算所有经销商）
}

// 经销商简要信息（用于下拉选择）
export interface DealerOption {
  id: number;
  name: string;
  code?: string;
}

// ==================== 设备管理相关类型 ====================

// 设备类型
export interface DeviceType {
  id: number;
  name: string;                    // 设备类型名称
  description?: string;            // 设备类型描述
  firm: string;                    // 设备厂商（唯一）
  apiConfig?: string;              // API配置JSON字符串
  deleted: boolean;                // 是否删除
  createdAt: string;               // 创建时间
  updatedAt: string;               // 更新时间
}

// 创建/更新设备类型请求
export interface DeviceTypeRequest {
  name: string;
  description?: string;
  firm: string;
  apiConfig?: string;
}

// 设备类型分页查询参数
export interface DeviceTypeQueryParams {
  pageNo?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

// 设备批次
export interface DeviceBatch {
  id: number;
  batchName: string;               // 批次名称
  batchStatus: string;             // 批次状态
  deviceTypeId: number;            // 设备类型ID
  deviceTypeName?: string;         // 设备类型名称（查询时返回）
  dealerId: number;                // 经销商ID
  dealerName?: string;             // 经销商名称（查询时返回）
  giftPackageGroupId?: number;     // 赠送套餐组ID
  giftPackageGroupName?: string;   // 赠送套餐组名称（查询时返回）
  payPackageGroupId?: number;      // 付费套餐组ID
  packageGroupName?: string;       // 付费套餐组名称（查询时返回）
  config?: string;                 // 配置信息JSON字符串
  deviceCount?: number;            // 设备数量
  deleted: boolean;                // 是否删除
  createdAt: string;               // 创建时间
  updatedAt: string;               // 更新时间
}

// 创建/更新设备批次请求
export interface DeviceBatchRequest {
  batchName: string;
  batchStatus?: string;
  deviceTypeId: number;
  dealerId: number;
  giftPackageGroupId?: number;
  payPackageGroupId?: number;
  config?: string;
  deviceCount?: number;
}

// 设备批次分页查询参数
export interface DeviceBatchQueryRequest {
  adminUserId: number;             // 管理员用户ID
  batchName?: string;              // 批次名称（模糊查询）
  deviceTypeId?: number;           // 设备类型ID
  dealerId?: number;               // 经销商ID
  batchStatus?: string;            // 批次状态
  payPackageGroupId?: number;      // 付费套餐组ID
  pageNo?: number;
  pageSize?: number;
}

// 设备
export interface Device {
  id: number;
  typeId: number;                  // 设备类型ID
  typeName?: string;               // 设备类型名称（查询时返回）
  firm?: string;                   // 设备厂商（查询时返回）
  batchId: number;                 // 设备批次ID
  batchName?: string;              // 批次名称（查询时返回）
  dealerId?: number;               // 经销商ID（查询时返回）
  dealerName?: string;             // 经销商名称（查询时返回）
  giftPackageGroupId?: number;     // 赠送套餐组ID（查询时返回）
  giftPackageGroupName?: string;   // 赠送套餐组名称（查询时返回）
  payPackageGroupId?: number;      // 付费套餐组ID（查询时返回）
  payPackageGroupName?: string;    // 付费套餐组名称（查询时返回）
  batchConfig?: string;            // 批次配置（查询时返回）
  deviceNum: string;               // 设备编号
  deleted: boolean;                // 是否删除
  createdAt: string;               // 创建时间
  updatedAt: string;               // 更新时间
}

// 创建/更新设备请求
export interface DeviceRequest {
  typeId: number;
  batchId: number;
  deviceNum: string;
}

// 设备分页查询参数
export interface DeviceQueryRequest {
  adminUserId: number;             // 管理员用户ID
  deviceNum?: string;              // 设备编号（模糊查询）
  typeId?: number;                 // 设备类型ID
  batchId?: number;                // 设备批次ID
  dealerId?: number;               // 经销商ID
  firm?: string;                   // 设备厂商
  typeName?: string;               // 设备类型名称（模糊查询）
  batchName?: string;              // 批次名称（模糊查询）
  dealerName?: string;             // 经销商名称（模糊查询）
  pageNo?: number;
  pageSize?: number;
}

// 设备导入错误信息
export interface ImportError {
  deviceNum: string;               // 设备编号
  errorMessage: string;            // 失败原因
}

// 设备导入结果
export interface ImportResult {
  totalCount: number;              // 总设备数量
  successCount: number;            // 新增成功数量
  updatedCount: number;            // 更新批次数量（已存在设备更新批次）
  failedCount: number;             // 导入失败数量
  failedDeviceNums: string[];      // 失败的设备编号列表
  failedExcelUrl?: string;         // 失败设备Excel文件下载地址（Base64编码）
  errors: ImportError[];           // 详细错误信息列表
}

// ==================== 微信小程序管理 ====================

export interface WxAppVO {
  id: number;
  appId: string;
  appSecret: string;
  appType: number;                // 1-小程序，2-公众号
  appTypeText: string;
  appName: string;
  appOriginalId?: string;
  appQrcode?: string;
  status: number;                 // 1-启用，0-禁用
  statusText: string;
  remark?: string;
  createBy?: string;
  updateBy?: string;
  dealerId?: number;
  createdAt?: string;
  updatedAt?: string;
  boundRelationId?: number;
  boundMerchantId?: number;
  boundMchId?: string;
  boundMerchantName?: string;
}

export interface WxAppQueryRequest {
  appName?: string;
  appType?: number;
  status?: number;
  pageNo: number;
  pageSize: number;
}

export interface WxAppSaveRequest {
  id?: number;
  appId: string;
  appSecret: string;
  appType: number;
  appName: string;
  appOriginalId?: string;
  appQrcode?: string;
  status: number;
  remark?: string;
}

export interface MerchantOption {
  id: number;
  mchId: string;
  mchName: string;
}

// ==================== 微信商户号管理 ====================

export interface WxMerchantVO {
  id: number;
  mchId: string;
  mchName: string;
  mchKey: string;
  certPath?: string;
  certKeyPath?: string;
  status: number;                 // 1-启用，0-禁用
  statusText: string;
  remark?: string;
  createBy?: string;
  updateBy?: string;
  dealerId?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface WxMerchantQueryRequest {
  mchName?: string;
  mchId?: string;
  status?: number;
  pageNo: number;
  pageSize: number;
}

export interface WxMerchantSaveRequest {
  id?: number;
  mchId: string;
  mchName: string;
  mchKey: string;
  certPath?: string;
  certKeyPath?: string;
  status: number;
  remark?: string;
}

// ==================== 支付通道配置 ====================

export type PaymentChannel =
  | 'WECHAT_MINIAPP'
  | 'WECHAT_H5'
  | 'WECHAT_QR'
  | 'ALIPAY_MINIAPP'
  | 'ALIPAY_H5'
  | 'H5_CASHIER'
  | 'YEEPAY'
  | 'YUNHUITENG';

export interface PaymentConfigResponse {
  paymentChannel: PaymentChannel;
}

export interface PaymentChannelSaveRequest {
  paymentChannel: PaymentChannel;
}

// ==================== 平台支付路由配置 ====================

export type ClientPlatform = 'WEAPP' | 'WECHAT_H5' | 'H5' | 'ALIPAY';

export type PaymentProvider = 'WECHAT_OFFICIAL' | 'YEEPAY' | 'ALIPAY_OFFICIAL' | 'YUNHUITENG';

export type PlatformPaymentMethod =
  | 'WECHAT_MINIAPP'
  | 'WECHAT_JSAPI'
  | 'ALIPAY_H5'
  | 'ALIPAY_MINIAPP';

export type PaymentScene = 'WEAPP' | 'WECHAT_H5' | 'H5' | 'ALIPAY';

export type PayActionType =
  | 'WECHAT_MINIAPP_PAY'
  | 'WECHAT_JSAPI_PAY'
  | 'ALIPAY_H5_FORM'
  | 'ALIPAY_MINIAPP_PAY';

export interface SensitiveStatus {
  appSecretConfigured?: boolean;
  appPrivateKeyConfigured?: boolean;
  alipayPublicKeyConfigured?: boolean;
  apiKeyConfigured?: boolean;
  privateKeyConfigured?: boolean;
  merchantPrivateKeyConfigured?: boolean;
  certificateConfigured?: boolean;
  merchantCertificateConfigured?: boolean;
  tokenConfigured?: boolean;
  appAuthTokenConfigured?: boolean;
  updatedAt?: string;
  certificateExpireAt?: string;
  summary?: string;
}

export type CredentialType = 'WECHAT_MINIAPP' | 'WECHAT_MP' | 'ALIPAY_APP';

export interface AppCredentialConfig {
  appCredentialId: number;
  /** 为空表示通用应用资质，可与任意服务商的商户配置组合 */
  paymentProvider?: PaymentProvider;
  clientPlatform: ClientPlatform;
  /** 客户端真实 AppID（云汇腾场景下为微信小程序 AppID） */
  appId: string;
  appName: string;
  credentialType: CredentialType;
  /** 云汇腾应用 ID（来自 configParams.gatewayAppId） */
  gatewayAppId?: string;
  configComplete: boolean;
  sensitiveStatus?: SensitiveStatus;
}

export interface MerchantConfig {
  merchantConfigId: number;
  paymentProvider: PaymentProvider;
  merchantNo?: string;
  subMerchantNo?: string;
  sellerId?: string;
  appAuthTokenConfigured?: boolean;
  /** 云汇腾 MD5 私钥是否已配置（来自 sensitiveMask.privateKey） */
  privateKeyConfigured?: boolean;
  /** 微信支付公钥 ID（来自 configParams.wechatPayPublicKeyId，明文可展示） */
  wechatPayPublicKeyId?: string;
  /** 微信支付公钥是否已配置（来自 sensitiveMask.wechatPayPublicKey） */
  wechatPayPublicKeyConfigured?: boolean;
  /** 微信支付公钥脱敏摘要，仅展示，禁止回传 */
  wechatPayPublicKeyMask?: string;
  /** 云汇腾应用 ID（子商户维度，来自 configParams.gatewayAppId） */
  gatewayAppId?: string;
  /** 引用本商户配置的启用路由当前下单实际使用的云汇腾应用 ID，仅展示 */
  effectiveGatewayAppId?: string;
  name: string;
  configComplete: boolean;
  sensitiveStatus?: SensitiveStatus;
}

export interface PlatformPaymentRoute {
  routeId?: number;
  clientPlatform: ClientPlatform;
  paymentMethod: PlatformPaymentMethod;
  paymentProvider: PaymentProvider;
  appCredentialId: number;
  merchantConfigId: number;
  enabled: boolean;
  displayName?: string;
  sortOrder?: number;
  configComplete?: boolean;
  /** 稳定路由作用域键；小程序为 APP:平台:真实AppID */
  routeScopeKey?: string;
  /** 路由元数据版本，探测/完整解析一致性用 */
  routeVersion?: number;
  /**
   * 启用路由时的乐观并发基线：锁后当前启用路由 ID；无启用路由时为 null。
   * 仅在 enabled=true 的保存请求中需要。
   */
  expectedActiveRouteId?: number | null;
}

export interface PlatformPaymentCapability {
  clientPlatform: ClientPlatform;
  paymentMethod: PlatformPaymentMethod;
  providers: PaymentProvider[];
  payActionType?: PayActionType;
}

export interface PlatformPaymentConfig {
  clientPlatform: ClientPlatform;
  supportedMethods: PlatformPaymentMethod[];
  capabilities?: PlatformPaymentCapability[];
  candidateRoutes: PlatformPaymentRoute[];
  enabledRouteId?: number;
  supportedProviders?: PaymentProvider[];
  enabledRouteByAppCredentialId?: Record<string, number>;
  appCredentialConfigs: AppCredentialConfig[];
  merchantConfigs: MerchantConfig[];
}

export interface PlatformConfigQueryRequest {
  clientPlatform?: ClientPlatform;
  paymentProvider?: PaymentProvider;
}

export interface PlatformPaymentConfigQueryResponse {
  platforms: PlatformPaymentConfig[];
  allAppCredentialConfigs?: AppCredentialConfig[];
  allMerchantConfigs?: MerchantConfig[];
}

export interface PlatformConfigSaveRequest {
  clientPlatform?: ClientPlatform;
  appCredentialConfigs?: AppCredentialConfigSaveRequest[];
  merchantConfigs?: MerchantConfigSaveRequest[];
  routes?: PlatformPaymentRoute[];
}

export interface AppCredentialConfigSaveRequest extends Partial<AppCredentialConfig> {
  appSecret?: string;
  appPrivateKey?: string;
  alipayPublicKey?: string;
  certificateContent?: string;
  /** 云汇腾应用 ID，写入服务端 configParams.gatewayAppId */
  gatewayAppId?: string;
}

export interface MerchantConfigSaveRequest extends Partial<MerchantConfig> {
  apiKey?: string;
  merchantPrivateKey?: string;
  merchantCertificateSerialNo?: string;
  merchantCertificateContent?: string;
  appAuthToken?: string;
  /** 云汇腾 MD5 业务私钥；留空表示不更新 */
  privateKey?: string;
  /** 微信支付公钥 PEM；留空表示不更新，禁止提交 mask */
  wechatPayPublicKey?: string;
}

export interface PaymentConfigAuditVO {
  id: number;
  clientPlatform: ClientPlatform;
  paymentProvider?: PaymentProvider;
  operatorId?: number;
  operatorName?: string;
  beforeConfig?: string;
  afterConfig?: string;
  appCredentialId?: number;
  merchantConfigId?: number;
  sensitiveChanged?: boolean;
  createTime: string;
}

export interface PaymentConfigAuditPageRequest {
  clientPlatform?: ClientPlatform;
  paymentProvider?: PaymentProvider;
  operatorId?: number;
  startTime?: string;
  endTime?: string;
  page: number;
  size: number;
}

// ==================== 易宝退款 ====================

export type YeepayRefundStatus =
  | 'PROCESSING'
  | 'SUCCESS'
  | 'FAILED'
  | 'CANCEL'
  | 'SUSPEND';

export interface YeepayRefundRecord {
  id: number;
  orderId: number;
  orderNo: string;
  refundRequestId: string;
  refundAmount: number;
  refundReason?: string;
  refundStatus: YeepayRefundStatus;
  yeepayUniqueRefundNo?: string;
  failureReason?: string;
  failReason?: string;
  refundTime?: string;
  thirdPartyTraceData?: string;
  createdAt?: string;
  updatedAt?: string;
  createTime?: string;
  updateTime?: string;
  deleted?: boolean;
}

export interface YeepayRefundCreateRequest {
  refundAmount: number;
  reason: string;
}